import React from "react";
import styled from "styled-components";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Paper,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import PixIcon from "@mui/icons-material/Pix";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";

type HowItWorksModalProps = {
  open: boolean;
  onClose: () => void;
};

const SectionCard = styled(Paper)<{ $color: string }>`
  border: 1px solid ${(p) => p.$color};
  background: #fff;
  border-radius: 12px !important;
  padding: 16px;
`;

const Badge = styled.div<{ $bg: string }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(p) => p.$bg};
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  margin-right: 12px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const Bullet = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  color: #202124;
  margin: 8px 0;
  svg {
    color: #34a853;
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

const FooterBox = styled(Paper)`
  background: #eef3ff;
  border: 1px solid #d6e4ff;
  border-radius: 12px !important;
  padding: 12px 16px;
`;

export const HowItWorksModal: React.FC<HowItWorksModalProps> = ({ open, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}>
        <InfoOutlinedIcon color="info" />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Como Funciona
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2, color: "#5f6368" }}>
          Ativação em 2 passos: 1) Adesão do agente; 2) Assinatura GeoRanking no cartão.
        </Typography>

        {/* Passo 1 */}
        <SectionCard elevation={0} $color="#d8ccff" sx={{ mb: 2 }}>
          <SectionHeader>
            <Badge $bg="#7c3aed">1</Badge>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Adesão do agente (pagamento único)
            </Typography>
          </SectionHeader>

          <Bullet>
            <CheckCircleOutlineIcon fontSize="small" />
            <Typography variant="body2">
              Você seleciona o agente pelo código (ex.: #123).
            </Typography>
          </Bullet>
          <Bullet>
            <CreditCardOutlinedIcon fontSize="small" />
            <Typography variant="body2">A adesão pode ser paga por CARTÃO ou PIX.</Typography>
          </Bullet>
          <Bullet>
            <CheckCircleOutlineIcon fontSize="small" />
            <Typography variant="body2">
              Cupom FRANQUIAS: quando aplicado, a adesão é isenta (não há cobrança).
            </Typography>
          </Bullet>
          <Bullet>
            <CheckCircleOutlineIcon fontSize="small" />
            <Typography variant="body2">
              A adesão é recebida diretamente pelo agente e a nota fiscal é emitida por ele.
            </Typography>
          </Bullet>
          <Bullet>
            <CheckCircleOutlineIcon fontSize="small" />
            <Typography variant="body2">
              Em caso de reembolso/estorno da adesão (CDC), a solicitação é feita pelos canais oficiais. O
              estorno é processado pelo agente no mesmo meio de pagamento, conforme regras do emissor.
            </Typography>
          </Bullet>
        </SectionCard>

        {/* Passo 2 */}
        <SectionCard elevation={0} $color="#ccebd7" sx={{ mb: 2 }}>
          <SectionHeader>
            <Badge $bg="#16a34a">2</Badge>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Assinatura do GeoRanking (recorrente)
            </Typography>
          </SectionHeader>

          <Bullet>
            <AutorenewIcon fontSize="small" />
            <Typography variant="body2">
              A mensalidade é cobrada MENSALMENTE no CARTÃO de crédito.
            </Typography>
          </Bullet>
          <Bullet>
            <CreditCardOutlinedIcon fontSize="small" />
            <Typography variant="body2">
              Se a adesão foi paga no cartão, você pode reutilizar o mesmo cartão na assinatura.
            </Typography>
          </Bullet>
          <Bullet>
            <PixIcon fontSize="small" />
            <Typography variant="body2">
              Se a adesão foi PIX (ou isenta), coletamos o cartão para a assinatura (sem cobrança
              imediata até a ativação).
            </Typography>
          </Bullet>
          <Bullet>
            <CheckCircleOutlineIcon fontSize="small" />
            <Typography variant="body2">
              A assinatura se renova automaticamente até cancelamento. Você pode cancelar a qualquer
              momento pelos canais oficiais.
            </Typography>
          </Bullet>
        </SectionCard>

        {/* Segurança */}
        <SectionCard elevation={0} $color="#fde7c3" sx={{ mb: 2 }}>
          <SectionHeader>
            <Badge $bg="#f59e0b">
              <LockOutlinedIcon sx={{ fontSize: 18 }} />
            </Badge>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Segurança
            </Typography>
          </SectionHeader>

          <Bullet>
            <LockOutlinedIcon fontSize="small" />
            <Typography variant="body2">
              Pagamentos e dados de cartão são processados pela Stripe. Não armazenamos o número
              completo do cartão.
            </Typography>
          </Bullet>
          <Bullet>
            <CheckCircleOutlineIcon fontSize="small" />
            <Typography variant="body2">
              Conformidade PCI SAQ-A (coleta via Stripe Elements/Checkout) e conexão segura (HTTPS/TLS).
            </Typography>
          </Bullet>
        </SectionCard>

        {/* Canais oficiais */}
        <FooterBox elevation={0}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <InfoOutlinedIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Canais oficiais
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CallOutlinedIcon fontSize="small" />
              Telefone/WhatsApp:{" "}
              <Link href="tel:08008001700" underline="hover">
                0800 800 1700
              </Link>
            </Typography>
            <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PolicyOutlinedIcon fontSize="small" />
              <Link href="#" underline="hover">
                Política de cancelamento e Ciclo de cobrança
              </Link>
            </Typography>
          </Box>
        </FooterBox>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="contained" onClick={onClose}>
            Entendi
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default HowItWorksModal;